import React, { useEffect, useCallback } from 'react'
import { DynamicComposedPage } from '@/pages/DynamicComposedPage'
import { useSearchParams } from 'react-router-dom'
import Cookies from 'js-cookie'
import { Locale, logger } from '@vrw/data/src/utils'
import { useContentBySlug } from '@/query/contentful/useContentPageBySlug'
import { FooterBlock, TextLinkKeys } from '@/components/Page/Footer/types'
import { useAppSelector } from '@/redux/hooks/hooks'
import { getIsAuthenticated } from '@vrw/data/src/redux/auth/selectors'
import { PATHS } from '@/router/paths'
import { ContentfulBlock } from '@/components/ContentfulBlock/types'
import { convertLocaleToContentfulLocale } from '@vrw/data/src/utils/contentfulPages'

const SLUG_NAME = PATHS.REWARDS_CREDIT_CARD.replace('/', '')
const COOKIE_EXPIRY_DAYS = 1

const PARAM_KEYS = {
  siteCode: 'siteCode',
  accorId: 'accorId',
}

export const COOKIE_KEYS = {
  siteCode: 'uscc_apply_sitecode',
  accorId: 'uscc_apply_accorid',
}
const getFooterLinkParams = (isAuthenticated: boolean, data?: ContentfulBlock<FooterBlock>) => {
  const footerLinks = data?.fields[isAuthenticated ? TextLinkKeys.privateFooterLinks : TextLinkKeys.publicFooterLinks]
  const footerUrl = footerLinks?.fields.link.find((link) => link.fields.url.includes(SLUG_NAME))?.fields.url
  const queryString = footerUrl?.split('?')[1] ?? ''
  const params = new URLSearchParams(queryString)
  const footerSiteCode = params.get(PARAM_KEYS.siteCode)
  const footerAccorId = params.get(PARAM_KEYS.accorId)
  return { footerSiteCode, footerAccorId }
}

const getStoredCookieValues = () => {
  let storedSiteCode: string | undefined
  let storedAccorId: string | undefined
  try {
    storedSiteCode = Cookies.get(COOKIE_KEYS.siteCode)
    storedAccorId = Cookies.get(COOKIE_KEYS.accorId)
  } catch (error) {
    logger.error('Error accessing cookies:', error)
  }
  return { storedSiteCode, storedAccorId }
}

export const CreditCardApply = () => {
  const isAuthenticated = useAppSelector(getIsAuthenticated)
  const { data } = useContentBySlug<FooterBlock>({ slug: 'footer', viewingRegion: convertLocaleToContentfulLocale(Locale.EN_US) })
  const { footerSiteCode, footerAccorId } = getFooterLinkParams(isAuthenticated, data)
  const { storedSiteCode, storedAccorId } = getStoredCookieValues()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlSiteCode = searchParams.get(PARAM_KEYS.siteCode)
  const urlAccorId = searchParams.get(PARAM_KEYS.accorId)

  const handleSiteCodeAndAccorId = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    let paramSource = null

    // check if data has loaded before handling params to avoid race condition
    if (!data) return

    if (!urlSiteCode) {
      // No siteCode param in URL - use footer values
      paramSource = { siteCode: footerSiteCode, accorId: footerAccorId }
    } else if (storedSiteCode && urlSiteCode !== storedSiteCode) {
      // URL siteCode param differs from stored cookies - use cookie values
      paramSource = { siteCode: storedSiteCode, accorId: storedAccorId }
    } else if (!storedSiteCode && urlSiteCode !== footerSiteCode) {
      // No stored siteCode and URL siteCode param differs from footer values - save URL values to cookies
      try {
        Cookies.set(COOKIE_KEYS.siteCode, urlSiteCode, { expires: COOKIE_EXPIRY_DAYS })
        if (urlAccorId) {
          Cookies.set(COOKIE_KEYS.accorId, urlAccorId, { expires: COOKIE_EXPIRY_DAYS })
        }
      } catch (error) {
        logger.error('Error setting cookies:', error)
      }
    }

    // Update URL if we have values to set
    if (paramSource) {
      if (paramSource.siteCode) newParams.set(PARAM_KEYS.siteCode, paramSource.siteCode)
      if (paramSource.accorId) newParams.set(PARAM_KEYS.accorId, paramSource.accorId)
      setSearchParams(newParams, { replace: true })
    }
  }, [searchParams, data, urlSiteCode, urlAccorId, storedSiteCode, storedAccorId, footerSiteCode, footerAccorId, setSearchParams])

  useEffect(() => {
    handleSiteCodeAndAccorId()
  }, [handleSiteCodeAndAccorId])

  return <DynamicComposedPage slugName={SLUG_NAME} />
}
